import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import type { AxisValueFormatterContext } from "@mui/x-charts/models";
import ChartContainer from "../common/ChartContainer";
import { useApi } from "../../hooks/useApi";
import { formatAxisDate } from "../../utils/chartDate";

export interface StatusChartResponse {
  chart: {
    xAxis: string[];
    series: Array<{
      label: string;
      data: number[];
      // Present only on charts that opt into splitByDryRun on the backend
      // (e.g. subscription_resolutions). Distinguishes simulated runs from real ones.
      dryRun?: boolean;
      // Present only on charts that opt into splitByPpType on the backend (e.g. actions).
      // null means the log row carries no pp_type.
      ppType?: string | null;
    }>;
  };
  summary: {
    total: number;
    avgPerDay: number;
    byStatus: Record<string, number>;
  };
}

// Raw status -> human label. Keys are backend RefundLogStatus values, plus chart series names
// for charts that aren't keyed by log status (e.g. auto_cancelled_to_cancelled).
export const STATUS_LABELS: Record<string, string> = {
  success: "Auto-Refunded",
  manual_success: "Manual Success",
  cancelled: "Auto-Cancelled",
  max_amount_exceeded: "Max Amount Exceeded (declined)",
  dry_run: "Read Only (skipped)",
  already_refunded: "Already Refunded (skipped)",
  duplicate_alert: "Duplicate Alert (auto-resolved)",
  already_refunded_no_duplicate: "Already Refunded, No Duplicate (auto-resolved)",
  multiple_transactions: "Multiple Transactions (skipped)",
  no_match: "No Match (skipped)",
  ambiguous: "Ambiguous (skipped)",
  ethoca_visa_skipped: "Visa Refund Disabled",
  manual_failed: "Manual Failed",
  match_failed: "Match Failed",
  refund_failed: "Refund Failed",
  coc_void_instead_of_refund: "Void Instead of Refund",
  coc_refund_unsettled: "Refund Not Settled",
  shopify_refund_unsettled: "Refund Not Settled",
  crm_refund_unsettled: "Refund Not Settled",
  coc_portal_refund_success: "Portal Refunded",
  coc_fulfillment_cancelled: "Fulfillment Cancelled",
  coc_shopify_order_cancelled: "Shopify Order Cancelled (via CoC)",
  shopify_order_cancelled: "Order Cancelled",
  coc_fulfillment_cancelled_dry_run: "Fulfillment Cancelled (read-only)",
  coc_shopify_order_cancelled_dry_run: "Shopify Order Cancelled (via CoC) (read-only)",
  shopify_order_cancelled_dry_run: "Order Cancelled (read-only)",
  crm_external_refund_recorded_dry_run: "External Refund Recorded (read-only)",
  crm_external_refund_already_refunded_dry_run: "External Refund Already Refunded (skipped) (read-only)",
  crm_external_refund_failed_dry_run: "External Refund Failed (read-only)",
  coc_external_refund_recorded_dry_run: "External Refund Recorded (read-only)",
  coc_external_refund_already_refunded_dry_run: "External Refund Already Refunded (skipped) (read-only)",
  coc_external_refund_failed_dry_run: "External Refund Failed (read-only)",
  refunded_externally_updated_dry_run: "Refunded Externally Updated (read-only)",
  coc_portal_refund_dry_run: "Portal Read Only (skipped)",
  coc_matched_on_other_account_dry_run: "Matched on Another CoC Account (read-only)",
  adyen_match_found_dry_run: "Found via Adyen (read-only)",
  coc_refund_pending: "Refund Pending (not settled)",
  crm_refund_pending: "Refund Pending (not settled)",
  shopify_refund_pending: "Refund Pending (not settled)",
  shopify_refund_verified: "Refund Verified",
  crm_refund_verified: "Refund Verified",
  coc_portal_refund_failed: "Portal Refund Failed",
  resolve_failed: "Resolve Failed",
  order_details_failed: "Order Details Failed",
  cancel_failed: "Cancel Failed",
  not_fulfilled: "Unfulfilled Order Not Cancelled",
  unexpected_fulfillment_status: "Unexpected Fulfillment",
  subscription_cancelled: "Manual Subscription Cancelled",
  subscription_not_subscription: "Manual Marked Not Subscription",
  subscription_not_found: "Manual Marked Not Found",
  refunded_externally_updated: "Refunded Externally Updated",
  coc_external_refund_recorded: "External Refund Recorded",
  coc_external_refund_already_refunded: "External Refund Already Refunded (skipped)",
  coc_external_refund_failed: "External Refund Failed",
  crm_subscription_cancelled: "Auto-Cancelled CRM Subscription",
  crm_subscription_already_cancelled: "CRM Subscription Already Cancelled",
  crm_not_subscription: "No Subscription on CRM Order",
  crm_subscription_failed: "CRM Cancel Failed",
  crm_order_cancelled: "Order Cancelled",
  crm_external_refund_recorded: "External Refund Recorded",
  crm_external_refund_already_refunded: "External Refund Already Refunded (skipped)",
  crm_external_refund_failed: "External Refund Failed",
  loop_cancelled: "Auto-Cancelled Subscription",
  loop_already_cancelled: "Subscription Already Cancelled",
  loop_not_subscription: "No Subscription on Order",
  loop_failed: "Loop Cancel Failed",
  kaching_cancelled: "Auto-Cancelled Kaching Contract",
  kaching_already_cancelled: "Kaching Contract Already Cancelled",
  kaching_not_subscription: "No Kaching Contract on Order",
  kaching_failed: "Kaching Cancel Failed",
  coc_voided: "Voided Authorization",
  coc_void_failed: "Void Failed",
  loop_auto_cancel_succeeded: "Cancelled",
  loop_auto_cancel_already_cancelled: "Already Cancelled",
  loop_auto_cancel_no_subscription: "No Subscription",
  loop_auto_cancel_skipped: "Skipped",
  loop_auto_cancel_failed: "Failed",
  kaching_auto_cancel_succeeded: "Cancelled (Kaching)",
  kaching_auto_cancel_already_cancelled: "Already Cancelled (Kaching)",
  kaching_auto_cancel_no_subscription: "No Contract (Kaching)",
  kaching_auto_cancel_skipped: "Skipped (Kaching)",
  kaching_auto_cancel_failed: "Failed (Kaching)",
  loop_auto_cancel_succeeded_dry_run: "Cancelled (read-only)",
  loop_auto_cancel_already_cancelled_dry_run: "Already Cancelled (read-only)",
  loop_auto_cancel_no_subscription_dry_run: "No Subscription (read-only)",
  loop_auto_cancel_skipped_dry_run: "Skipped (read-only)",
  loop_auto_cancel_failed_dry_run: "Failed (read-only)",
  kaching_auto_cancel_succeeded_dry_run: "Cancelled (Kaching) (read-only)",
  kaching_auto_cancel_already_cancelled_dry_run: "Already Cancelled (Kaching) (read-only)",
  kaching_auto_cancel_no_subscription_dry_run: "No Contract (Kaching) (read-only)",
  kaching_auto_cancel_skipped_dry_run: "Skipped (Kaching) (read-only)",
  kaching_auto_cancel_failed_dry_run: "Failed (Kaching) (read-only)",
  auto_cancelled_to_cancelled: "Cancelled (Auto) → Cancelled",
  adyen_match_found: "Found via Adyen",
  adyen_match_not_in_coc: "Adyen Only (not in CoC)",
  coc_matched_on_other_account: "Matched on Another CoC Account",
};

// Desired display order — top of legend / base of stack first.
// Tiers: greens/blue (success-like) -> yellows/amber (soft warnings) -> reds (failures).
export const STATUS_ORDER: string[] = [
  // greens / blue (success-like)
  "success",
  "cancelled",
  "max_amount_exceeded",
  "duplicate_alert",
  "already_refunded_no_duplicate",
  "manual_success",
  "loop_cancelled",
  "kaching_cancelled",
  "loop_already_cancelled",
  "kaching_already_cancelled",
  "loop_not_subscription",
  "kaching_not_subscription",
  "crm_subscription_cancelled",
  "crm_subscription_already_cancelled",
  "crm_not_subscription",
  "crm_order_cancelled",
  "crm_external_refund_recorded",
  "loop_auto_cancel_succeeded",
  "kaching_auto_cancel_succeeded",
  "loop_auto_cancel_already_cancelled",
  "kaching_auto_cancel_already_cancelled",
  "loop_auto_cancel_no_subscription",
  "kaching_auto_cancel_no_subscription",
  "subscription_cancelled",
  "subscription_not_subscription",
  "subscription_not_found",
  "refunded_externally_updated",
  "auto_cancelled_to_cancelled",
  "ethoca_visa_skipped",
  "coc_voided",
  "coc_external_refund_recorded",
  "adyen_match_found",
  "coc_matched_on_other_account",
  "coc_portal_refund_success",
  "coc_fulfillment_cancelled",
  "coc_shopify_order_cancelled",
  "shopify_order_cancelled",
  // yellows / amber (soft warnings & skips)
  "dry_run",
  "coc_portal_refund_dry_run",
  "coc_fulfillment_cancelled_dry_run",
  "coc_shopify_order_cancelled_dry_run",
  "shopify_order_cancelled_dry_run",
  "coc_matched_on_other_account_dry_run",
  "adyen_match_found_dry_run",
  "crm_external_refund_recorded_dry_run",
  "crm_external_refund_already_refunded_dry_run",
  "crm_external_refund_failed_dry_run",
  "coc_external_refund_recorded_dry_run",
  "coc_external_refund_already_refunded_dry_run",
  "coc_external_refund_failed_dry_run",
  "refunded_externally_updated_dry_run",
  "coc_refund_pending",
  "crm_refund_pending",
  "shopify_refund_pending",
  "already_refunded",
  "coc_external_refund_already_refunded",
  "crm_external_refund_already_refunded",
  "multiple_transactions",
  "not_fulfilled",
  "no_match",
  "ambiguous",
  "adyen_match_not_in_coc",
  "loop_auto_cancel_skipped",
  "kaching_auto_cancel_skipped",
  "loop_auto_cancel_succeeded_dry_run",
  "kaching_auto_cancel_succeeded_dry_run",
  "loop_auto_cancel_already_cancelled_dry_run",
  "kaching_auto_cancel_already_cancelled_dry_run",
  "loop_auto_cancel_no_subscription_dry_run",
  "kaching_auto_cancel_no_subscription_dry_run",
  "loop_auto_cancel_skipped_dry_run",
  "kaching_auto_cancel_skipped_dry_run",
  "loop_auto_cancel_failed_dry_run",
  "kaching_auto_cancel_failed_dry_run",
  // reds
  "match_failed",
  "unexpected_fulfillment_status",
  "manual_failed",
  "refund_failed",
  "resolve_failed",
  "order_details_failed",
  "cancel_failed",
  "loop_failed",
  "kaching_failed",
  "loop_auto_cancel_failed",
  "kaching_auto_cancel_failed",
  "coc_void_instead_of_refund",
  "coc_void_failed",
  "coc_external_refund_failed",
  "crm_subscription_failed",
  "crm_external_refund_failed",
  "coc_refund_unsettled",
  "shopify_refund_unsettled",
  "crm_refund_unsettled",
  "coc_portal_refund_failed",
];

// Deterministic color per raw status. Palette intent:
// emerald/teal/blue = success-like,
// yellow/amber/orange = soft warnings / benign skips,
// rose = failures (light->dark by upstream position).
export const STATUS_COLORS: Record<string, string> = {
  success: "#10b981",              // emerald-500
  cancelled: "#14b8a6",            // teal-500 (Auto-Cancelled — success via cancel)
  max_amount_exceeded: "#f97316",  // orange-500 (auto-refund declined — over limit)
  duplicate_alert: "#f59e0b",      // amber-500 — matches Automation Rate %
  already_refunded_no_duplicate: "#fbbf24",
  manual_success: "#465fff",       // MUI primary.main — matches the Ethoca/CDRN volume charts
  loop_cancelled: "#0d9488",       // teal-600 (auto subscription cancel — success)
  kaching_cancelled: "#0891b2",    // cyan-600
  loop_already_cancelled: "#0f766e", // teal-700
  kaching_already_cancelled: "#0e7490", // cyan-700
  loop_not_subscription: "#5eead4", // teal-300
  kaching_not_subscription: "#67e8f9", // cyan-300
  subscription_cancelled: "#1e40af",     // indigo/blue — manual subscription cancel
  subscription_not_subscription: "#3b82f6", // blue-500
  subscription_not_found: "#60a5fa",     // blue-400
  refunded_externally_updated: "#0ea5e9", // sky-500
  ethoca_visa_skipped: "#059669",  // emerald-600 (auto-resolved as Other / Not Refunded — success-like outcome)
  dry_run: "#fcd34d",              // amber-300 (Read Only — benign skip)
  already_refunded: "#fef08a",     // yellow-200
  multiple_transactions: "#fde047", // yellow-300
  not_fulfilled: "#facc15",        // yellow-400
  no_match: "#fbbf24",             // amber-400
  ambiguous: "#fb923c",            // orange-400
  match_failed: "#ef4444",         // red-500 (matching errored — needs review)
  unexpected_fulfillment_status: "#fecdd3", // rose-200
  manual_failed: "#fda4af",        // rose-300
  refund_failed: "#fb7185",        // rose-400
  coc_void_instead_of_refund: "#881337", // rose-900 (CoC voided instead of refunding — nothing confirmed returned)
  coc_refund_unsettled: "#7f1d1d",  // red-900 (refund accepted but never settled at the processor)
  coc_portal_refund_success: "#14b8a6",  // teal-500
  coc_fulfillment_cancelled: "#65a30d",  // lime-600
  coc_shopify_order_cancelled: "#0891b2",    // cyan-600
  shopify_order_cancelled: "#0e7490",    // cyan-700
  coc_portal_refund_dry_run: "#fbbf24",  // amber-400
  coc_fulfillment_cancelled_dry_run: "#eab308",    // yellow-500
  coc_shopify_order_cancelled_dry_run: "#ca8a04",  // yellow-600
  shopify_order_cancelled_dry_run: "#a16207",  // yellow-700
  coc_matched_on_other_account_dry_run: "#facc15",  // yellow-400
  adyen_match_found_dry_run: "#facc15",  // yellow-400
  crm_external_refund_recorded_dry_run: "#fde047",  // yellow-300
  crm_external_refund_already_refunded_dry_run: "#fef08a",  // yellow-200
  crm_external_refund_failed_dry_run: "#fdba74",  // orange-300
  coc_external_refund_recorded_dry_run: "#b45309",  // amber-700
  coc_external_refund_already_refunded_dry_run: "#fef9c3",  // yellow-100
  coc_external_refund_failed_dry_run: "#fb923c",  // orange-400
  refunded_externally_updated_dry_run: "#854d0e",  // yellow-800
  coc_refund_pending: "#f59e0b",  // amber-500 (reversal submitted, not settled)
  crm_refund_pending: "#f59e0b",
  shopify_refund_unsettled: "#991b1b",  // red-800 (kept distinct from the CoC series beside it)
  crm_refund_unsettled: "#991b1b",
  shopify_refund_pending: "#d97706",    // amber-600
  shopify_refund_verified: "#22c55e",   // green-500
  crm_refund_verified: "#22c55e",
  coc_portal_refund_failed: "#e11d48",   // rose-600
  resolve_failed: "#f43f5e",       // rose-500
  order_details_failed: "#e11d48", // rose-600
  cancel_failed: "#be123c",        // rose-700
  loop_failed: "#9f1239",          // rose-800
  kaching_failed: "#881337",       // rose-900
  coc_voided: "#34d399",           // emerald-400 (uncaptured auth voided — money never taken)
  coc_external_refund_recorded: "#6ee7b7", // emerald-300
  coc_external_refund_already_refunded: "#fef3c7", // amber-100
  coc_external_refund_failed: "#dc2626", // red-600
  coc_void_failed: "#4c0519",      // rose-950 (auth may still capture — needs manual review)
  crm_subscription_cancelled: "#0891b2",  // cyan-600
  crm_subscription_already_cancelled: "#0e7490",  // cyan-700
  crm_not_subscription: "#67e8f9",  // cyan-300
  crm_order_cancelled: "#65a30d",  // lime-600
  crm_external_refund_recorded: "#14b8a6",  // teal-500
  crm_external_refund_already_refunded: "#f59e0b",  // amber-500
  crm_subscription_failed: "#9f1239",  // rose-800
  crm_external_refund_failed: "#e11d48",  // rose-600
  loop_auto_cancel_succeeded: "#10b981",        // emerald-500 (matches `success`)
  kaching_auto_cancel_succeeded: "#06b6d4",     // cyan-500
  loop_auto_cancel_already_cancelled: "#0d9488", // teal-600 (matches alert-driven loop_cancelled)
  kaching_auto_cancel_already_cancelled: "#0891b2", // cyan-600
  loop_auto_cancel_no_subscription: "#5eead4",  // teal-300 (matches alert-driven loop_not_subscription)
  kaching_auto_cancel_no_subscription: "#67e8f9", // cyan-300
  loop_auto_cancel_skipped: "#fcd34d",          // amber-300 (benign skip)
  kaching_auto_cancel_skipped: "#fbbf24",       // amber-400
  loop_auto_cancel_failed: "#f43f5e",           // rose-500 (matches resolve_failed)
  kaching_auto_cancel_failed: "#e11d48",        // rose-600
  // dry-run variants — yellow-amber tier (no real action taken)
  loop_auto_cancel_succeeded_dry_run: "#fde047",         // yellow-300
  kaching_auto_cancel_succeeded_dry_run: "#facc15",      // yellow-400
  loop_auto_cancel_already_cancelled_dry_run: "#fef08a", // yellow-200
  kaching_auto_cancel_already_cancelled_dry_run: "#fde047", // yellow-300
  loop_auto_cancel_no_subscription_dry_run: "#fef9c3",   // yellow-100
  kaching_auto_cancel_no_subscription_dry_run: "#fef08a", // yellow-200
  loop_auto_cancel_skipped_dry_run: "#fef9c3",           // yellow-100
  kaching_auto_cancel_skipped_dry_run: "#fef08a",        // yellow-200
  loop_auto_cancel_failed_dry_run: "#fdba74",            // orange-300
  kaching_auto_cancel_failed_dry_run: "#fb923c",         // orange-400
  auto_cancelled_to_cancelled: "#465fff",
  adyen_match_found: "#4ade80",                          // green-400
  adyen_match_not_in_coc: "#fde68a",                     // amber-200
  coc_matched_on_other_account: "#7dd3fc",               // sky-300
};

const FALLBACK_PALETTE = [
  "#465fff", "#38bdf8", "#10b981", "#fbbf24", "#f43f5e",
  "#a855f7", "#ec4899", "#14b8a6", "#fb923c", "#84cc16",
];

// Payment-processor suffix appended to a status label on pp-type-split charts.
// Declaration order doubles as display order; anything unmapped sorts after these, null last.
const PP_TYPE_LABELS: Record<string, string> = {
  shopify: "Shopify",
  checkout_champ: "CoC",
  crm_connector: "CRM Connector",
};

const PP_TYPE_ORDER = Object.keys(PP_TYPE_LABELS);

export function formatLabel(status: string): string {
  return STATUS_LABELS[status] ?? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatPpTypeLabel(status: string, ppType: string | null): string {
  const suffix = ppType === null ? "no PP Type" : PP_TYPE_LABELS[ppType] ?? ppType;
  return `${formatLabel(status)} (${suffix})`;
}

function ppTypeOrderIndex(ppType: string | null | undefined): number {
  if (ppType == null) return PP_TYPE_ORDER.length + 1;
  const i = PP_TYPE_ORDER.indexOf(ppType);
  return i === -1 ? PP_TYPE_ORDER.length : i;
}

export function colorFor(status: string, index: number): string {
  return STATUS_COLORS[status] ?? FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
}

// Unknown statuses go after the known ones, in alphabetical order.
export function orderIndex(status: string): number {
  const i = STATUS_ORDER.indexOf(status);
  return i === -1 ? STATUS_ORDER.length : i;
}

export type ChartSource = "ethoca" | "cdrn" | "rdr";

// Fetches the requested alert-source status charts for the given variant in parallel.
export function useAlertSourceStatusCharts(
  variant: "actions" | "resolutions_by_alert_date" | "subscription_resolutions" | "subscription-auto-to-manual",
  sources: ChartSource[],
  params: { startDate: string; endDate: string; companyId: number | null },
) {
  const { get, loading } = useApi();
  const [data, setData] = useState<Partial<Record<ChartSource, StatusChartResponse | null>>>({});
  const { startDate, endDate, companyId } = params;
  const sourcesKey = sources.join(",");

  const fetchChartData = useCallback(async () => {
    if (!startDate || !endDate) return;

    const qs = new URLSearchParams({
      startDate,
      endDate,
      ...(companyId && { companyId: companyId.toString() }),
    }).toString();

    const results = await Promise.allSettled(
      sources.map((source) => get<StatusChartResponse>(`/api/alerts/${source}/${variant}/chart?${qs}`)),
    );

    const next: Partial<Record<ChartSource, StatusChartResponse | null>> = {};
    results.forEach((result, i) => {
      const source = sources[i];
      if (result.status === "fulfilled") {
        next[source] = result.value;
      } else {
        console.error(`Failed to fetch ${source} ${variant} chart data:`, result.reason);
        next[source] = null;
      }
    });
    setData(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, companyId, get, variant, sourcesKey]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return { data, loading };
}

interface StatusStackedBarChartProps {
  title: string;
  data: StatusChartResponse | null;
  loading: boolean;
}

export function StatusStackedBarChart({ title, data, loading }: StatusStackedBarChartProps) {
  const theme = useTheme();

  const chartSeries = useMemo(() => {
    if (!data) return [];
    // Sort by STATUS_ORDER. Drives both legend/tooltip order AND stacking —
    // first series is the base of the stack.
    const sorted = [...data.chart.series].sort(
      (a, b) =>
        orderIndex(a.label) - orderIndex(b.label)
        || a.label.localeCompare(b.label)
        || ppTypeOrderIndex(a.ppType) - ppTypeOrderIndex(b.ppType),
    );
    // Colour is keyed on the distinct status, not the series position, so the pp-type variants
    // of one status share a colour even when they fall back to the palette.
    const statusIndex = new Map<string, number>();
    for (const s of sorted) {
      if (!statusIndex.has(s.label)) statusIndex.set(s.label, statusIndex.size);
    }
    return sorted.map((s) => ({
      data: s.data,
      label: "ppType" in s ? formatPpTypeLabel(s.label, s.ppType ?? null) : formatLabel(s.label),
      color: colorFor(s.label, statusIndex.get(s.label)!),
      stack: "total",
      // MUI x-charts' axis tooltip skips rows whose formattedValue is null —
      // returning null for zeros hides empty categories from per-day tooltips.
      valueFormatter: (v: number | null) =>
        v == null || v === 0 ? null : v.toLocaleString(),
    }));
  }, [data]);

  const hasData = !!data && data.summary.total > 0;

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      <ChartContainer title={title} loading={loading}>
        {hasData ? (
          <BarChart
            xAxis={[
              {
                data: data!.chart.xAxis,
                valueFormatter: (value: string, ctx: AxisValueFormatterContext) => formatAxisDate(value, ctx.location),
                tickLabelStyle: { fontSize: 11, fill: theme.palette.text.secondary },
              },
            ]}
            yAxis={[
              {
                tickLabelStyle: { fontSize: 12, fill: theme.palette.text.secondary },
              },
            ]}
            series={chartSeries}
            height={340}
            borderRadius={3}
            slotProps={{
              legend: {
                direction: "horizontal",
                position: { vertical: "bottom", horizontal: "center" },
                sx: { fontSize: 11 },
              },
            }}
            margin={{ bottom: 60 }}
          />
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: 300 }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}
      </ChartContainer>
    </Box>
  );
}
